/* The note card is the one card that stretches: in the wide three-column day view its column has
   a minimum height of one screen (DayView.module.css), and the note takes the room the other
   cards leave, so the column's bottom meets the bottom of the viewport. The text grows with its
   content and never scrolls on its own (docs/design/scrolling.md). Doubled selector: it must win
   over `Card`'s own `flex: none`. */
.card.card {
  flex: 1 0 auto;
}

.editRegion {
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

/* Reading and editing share the box model and the typography, so switching between them does not
   shift the layout; the frame stays visible in every state (design 2026-10-16 §2.2). */
.textarea,
.editor,
.reading {
  flex: 1 0 auto;
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--surface);
  line-height: 1.5;
  /* Four lines, plus padding and border; the height is then set from the content. */
  min-height: calc(4 * 1.5em + 18px);
}

.textarea:focus-visible,
.editor:focus-within {
  border-color: var(--accent);
  outline: 2px solid var(--accent);
  outline-offset: -1px;
}

.editor {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* iOS Safari zooms into a focused field whose text is smaller than 16px (the textarea has its own rule in global.css). */
@media (pointer: coarse) {
  .editor :global(.cm-content) {
    font-size: 16px;
  }
}

.textarea {
  overflow-y: hidden;
  resize: none;
}

.textarea::placeholder {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

.reading {
  min-width: 0;
  cursor: text;
  overflow-wrap: anywhere;
}

.reading:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 1px;
}

/* The row under the editor, only while editing: status on the left, 取消 and 保存 on the right. */
.actions {
  display: flex;
  flex: none;
  align-items: center;
  gap: 8px;
}

.status {
  flex: 1 1 auto;
  min-width: 0;
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

.actionButton {
  flex: none;
}

.plain {
  white-space: pre-wrap;
}

/* ---- rendered Markdown ---- */
.markdown {
  min-width: 0;
}

.markdown > :first-child {
  margin-top: 0;
}

.markdown > :last-child {
  margin-bottom: 0;
}

.markdown p,
.markdown ul,
.markdown ol,
.markdown blockquote,
.markdown pre,
.markdown table {
  margin: 0 0 0.5em;
}

.markdown h1,
.markdown h2,
.markdown h3,
.markdown h4,
.markdown h5,
.markdown h6 {
  margin: 0.6em 0 0.3em;
  font-weight: 600;
  line-height: 1.3;
}

.markdown h1 {
  font-size: 1.25em;
}

.markdown h2 {
  font-size: 1.15em;
}

.markdown h3,
.markdown h4,
.markdown h5,
.markdown h6 {
  font-size: 1em;
}

.markdown ul,
.markdown ol {
  padding-left: 1.4em;
}

.markdown :global(.contains-task-list) {
  padding-left: 0;
  list-style: none;
}

.markdown :global(.task-list-item) {
  list-style: none;
}

.markdown :global(.task-list-item) input {
  margin: 0 0.4em 0 0;
  vertical-align: middle;
  accent-color: var(--accent);
}

.markdown a {
  color: var(--accent-700);
  text-decoration: underline;
}

.markdown blockquote {
  padding-left: 0.8em;
  border-left: 3px solid var(--border-control);
  color: var(--text-muted);
}

.markdown code {
  padding: 0 0.3em;
  border-radius: var(--radius-xs);
  background: var(--surface-muted);
  font-size: 0.92em;
}

.markdown pre {
  padding: 8px 10px;
  border-radius: var(--radius-sm);
  background: var(--surface-muted);
  /* No sideways scrolling anywhere (docs/design/scrolling.md): long lines wrap. */
  white-space: pre-wrap;
}

.markdown pre code {
  padding: 0;
  background: transparent;
}

.markdown hr {
  margin: 0.6em 0;
  border: 0;
  border-top: 1px solid var(--border);
}

.markdown table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

.markdown th,
.markdown td {
  padding: 3px 6px;
  border: 1px solid var(--border);
  text-align: left;
  overflow-wrap: anywhere;
}

.markdown th {
  background: var(--surface-muted);
  font-weight: 600;
}

.markdown del {
  color: var(--text-muted);
}
